// MessageThread — shared chat component used in broker portal (per client)
// and in seller/buyer apps (inbox from broker).
//
// Props:
//   brokerId   — UUID of the broker (Supabase user ID)
//   clientId   — UUID of the client (Supabase user ID)
//   myRole     — "broker" | "seller" | "buyer"
//   clientName — display name for the other party
//
// Brokers can send two message types:
//   "chat"   — regular message bubble
//   "action" — task card with "Mark done ✓" button on the client side

import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { openDisclosurePDF } from "../lib/disclosurePDF";

const mono = "'DM Mono',monospace";
const sans = "'Outfit','Inter',sans-serif";

const fmtTime = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

export default function MessageThread({ brokerId, clientId, myRole, clientName }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody]             = useState("");
  const [msgType, setMsgType]       = useState("chat");
  const [actionTitle, setActionTitle] = useState("");
  const [loading, setLoading]       = useState(true);
  const [sending, setSending]       = useState(false);
  const bottomRef = useRef(null);

  const otherLabel = myRole === "broker" ? (clientName || "Client") : "Your broker";

  useEffect(() => {
    if (!supabase || !brokerId || !clientId) { setLoading(false); return; }
    load();
    const channel = supabase
      .channel(`msgs:${brokerId}:${clientId}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "messages",
        filter: `broker_id=eq.${brokerId}`,
      }, (payload) => {
        if (payload.new.client_id === clientId)
          setMessages((prev) => [...prev, payload.new]);
      })
      .on("postgres_changes", {
        event: "UPDATE", schema: "public", table: "messages",
        filter: `broker_id=eq.${brokerId}`,
      }, (payload) => {
        if (payload.new.client_id === clientId)
          setMessages((prev) => prev.map((m) => m.id === payload.new.id ? payload.new : m));
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [brokerId, clientId]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("broker_id", brokerId)
      .eq("client_id", clientId)
      .order("created_at", { ascending: true });
    setMessages(data || []);
    setLoading(false);
    // Mark unread chat messages as read
    await supabase
      .from("messages")
      .update({ read_at: new Date().toISOString() })
      .eq("broker_id", brokerId)
      .eq("client_id", clientId)
      .eq("message_type", "chat")
      .neq("from_role", myRole)
      .is("read_at", null);
  };

  const send = async (e) => {
    e?.preventDefault();
    const isAction = myRole === "broker" && msgType === "action";
    if (isAction && !actionTitle.trim()) return;
    if (!isAction && !body.trim()) return;
    if (!supabase || !brokerId || !clientId) return;
    setSending(true);
    const msg = {
      broker_id: brokerId,
      client_id: clientId,
      from_role: myRole,
      body: body.trim() || actionTitle.trim(),
      message_type: isAction ? "action" : "chat",
      action_title: isAction ? actionTitle.trim() : null,
    };
    const { data } = await supabase.from("messages").insert(msg).select().single();
    if (data) setMessages((prev) => [...prev, data]);
    setBody("");
    setActionTitle("");
    setSending(false);
  };

  const markDone = async (msg) => {
    const { data } = await supabase
      .from("messages")
      .update({ completed_at: new Date().toISOString(), completed_by: myRole })
      .eq("id", msg.id)
      .select()
      .single();
    if (data) setMessages((prev) => prev.map((m) => m.id === data.id ? data : m));
  };

  // ── Fallback states ─────────────────────────────────────────────────────────

  if (!supabase) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:180, color:"#9bbfb2", fontFamily:mono, fontSize:12, textAlign:"center", padding:20 }}>
        Messaging requires Supabase auth.<br />Add REACT_APP_SUPABASE_URL to enable.
      </div>
    );
  }

  if (!brokerId || !clientId) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:180, color:"#9bbfb2", fontFamily:mono, fontSize:12, textAlign:"center", padding:20, lineHeight:1.7 }}>
        {!brokerId ? "Not connected to a broker yet.\nOnce your broker invites you, messaging unlocks here." :
         "Client hasn't joined 6zero.ai yet.\nOnce they sign in, messaging unlocks here."}
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", minHeight:0 }}>

      {/* Message list */}
      <div style={{ flex:1, overflowY:"auto", padding:"14px 16px", display:"flex", flexDirection:"column", gap:10 }}>
        {loading && (
          <div style={{ textAlign:"center", color:"#9bbfb2", fontFamily:mono, fontSize:11, padding:20 }}>Loading…</div>
        )}
        {!loading && messages.length === 0 && (
          <div style={{ textAlign:"center", color:"#9bbfb2", fontFamily:mono, fontSize:11, padding:20 }}>
            No messages yet. Send the first one.
          </div>
        )}

        {messages.map((m) => {
          // ── Disclosure document card ───────────────────────────────────────
          if (m.message_type === "disclosure") {
            let doc = {};
            try { doc = JSON.parse(m.body); } catch {}
            return (
              <div key={m.id} style={{ border: "1.5px solid #2d7a62", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ background: "#f2f8f5", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 20 }}>📋</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1c2b25", fontFamily: sans }}>Disclosure Docket</div>
                    <div style={{ fontSize: 11, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.04em" }}>{doc.address}</div>
                  </div>
                  <button
                    onClick={() => openDisclosurePDF(doc.address, doc.questions, doc.answers, doc.summary)}
                    style={{ background: "#2d7a62", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: sans, whiteSpace: "nowrap" }}
                  >
                    View / Print →
                  </button>
                </div>
                <div style={{ padding: "10px 14px", background: "#fff" }}>
                  <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>Forms covered</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["TDS", "SPQ", "NHD", "LEAD", "HOA"].map((f) => (
                      <span key={f} style={{ fontSize: 10, fontFamily: mono, background: "#f2f8f5", color: "#2d7a62", padding: "2px 7px", borderRadius: 4, letterSpacing: "0.06em" }}>{f}</span>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", marginTop: 8 }}>
                    Generated {fmtTime(doc.generated_at || m.created_at)}
                  </div>
                </div>
              </div>
            );
          }

          // ── Action item card ───────────────────────────────────────────────
          if (m.message_type === "action") {
            const done = Boolean(m.completed_at);
            const canAct = myRole !== "broker" && !done;
            return (
              <div key={m.id}>
                <div style={{
                  background: done ? "#f0faf5" : "#fff",
                  border: `1.5px solid ${done ? "#7ecfb0" : "#2d7a62"}`,
                  borderRadius: 12, padding: "12px 14px",
                }}>
                  <div style={{ fontSize:10, fontFamily:mono, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:5, fontWeight:700, color: done ? "#7ecfb0" : "#2d7a62" }}>
                    {done ? "✓ Done" : "☐ Action item"}
                  </div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1c2b25", marginBottom: (m.body && m.body !== m.action_title) ? 4 : 0 }}>
                    {m.action_title || m.body}
                  </div>
                  {m.body && m.body !== m.action_title && (
                    <div style={{ fontSize:12, color:"#5a8a78", lineHeight:1.6 }}>{m.body}</div>
                  )}
                  {done ? (
                    <div style={{ fontSize:10, fontFamily:mono, color:"#9bbfb2", marginTop:8 }}>
                      Completed · {fmtTime(m.completed_at)}
                    </div>
                  ) : canAct ? (
                    <button onClick={() => markDone(m)} style={{
                      marginTop:10, background:"#2d7a62", color:"#fff", border:"none",
                      borderRadius:8, padding:"7px 16px", fontSize:12, fontWeight:700,
                      cursor:"pointer", fontFamily:sans,
                    }}>
                      Mark done ✓
                    </button>
                  ) : null}
                </div>
                <div style={{ fontSize:10, fontFamily:mono, color:"#9bbfb2", marginTop:3, letterSpacing:"0.04em" }}>
                  {m.from_role === myRole ? "You" : otherLabel} · {fmtTime(m.created_at)}
                </div>
              </div>
            );
          }

          // ── Chat bubble ────────────────────────────────────────────────────
          const mine = m.from_role === myRole;
          return (
            <div key={m.id} style={{ display:"flex", flexDirection:"column", alignItems: mine ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth:"78%", background: mine ? "#1c5448" : "#f2f8f5",
                color: mine ? "#d4f5e8" : "#1c2b25",
                borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                padding:"10px 13px", fontSize:13, lineHeight:1.55, fontFamily:sans,
              }}>
                {m.body}
              </div>
              <div style={{ fontSize:10, fontFamily:mono, color:"#9bbfb2", marginTop:3, letterSpacing:"0.04em" }}>
                {mine ? "You" : otherLabel} · {fmtTime(m.created_at)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <form onSubmit={send} style={{ borderTop:"1px solid #f0f4f2", flexShrink:0 }}>
        {/* Type toggle — broker only */}
        {myRole === "broker" && (
          <div style={{ display:"flex", gap:6, padding:"8px 14px 0" }}>
            {[["chat", "Message"], ["action", "Action item"]].map(([t, label]) => (
              <button key={t} type="button" onClick={() => setMsgType(t)} style={{
                background: msgType === t ? (t === "action" ? "#2d7a62" : "#f2f8f5") : "none",
                color: msgType === t ? (t === "action" ? "#fff" : "#1c2b25") : "#9bbfb2",
                border: msgType === t ? "none" : "1px solid #d8ede6",
                borderRadius:6, padding:"4px 12px", fontSize:11, fontFamily:mono,
                cursor:"pointer", letterSpacing:"0.06em", fontWeight: msgType === t ? 700 : 400,
                transition:"all 0.1s",
              }}>
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Action title field */}
        {myRole === "broker" && msgType === "action" && (
          <div style={{ padding:"8px 14px 0" }}>
            <input
              value={actionTitle}
              onChange={(e) => setActionTitle(e.target.value)}
              placeholder="Action title — e.g. Upload disclosures"
              style={{ width:"100%", background:"#f7faf8", border:"1.5px solid #2d7a62", borderRadius:8, padding:"8px 12px", fontSize:13, fontFamily:sans, color:"#1c2b25", outline:"none", boxSizing:"border-box" }}
            />
          </div>
        )}
        {/* Body + send */}
        <div style={{ display:"flex", gap:8, padding:"10px 14px" }}>
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={msgType === "action" ? "Details (optional)…" : `Message ${otherLabel}…`}
            style={{ flex:1, background:"#f7faf8", border:"1.5px solid #d8ede6", borderRadius:8, padding:"9px 12px", fontSize:13, fontFamily:sans, color:"#1c2b25", outline:"none" }}
            onFocus={(e) => e.target.style.borderColor = "#2d7a62"}
            onBlur={(e) => e.target.style.borderColor = "#d8ede6"}
            disabled={sending}
          />
          <button type="submit"
            disabled={sending || (msgType === "action" && myRole === "broker" ? !actionTitle.trim() : !body.trim())}
            style={{ background:"#2d7a62", color:"#fff", border:"none", borderRadius:8, padding:"9px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:sans, opacity:(sending || (msgType === "action" && myRole === "broker" ? !actionTitle.trim() : !body.trim())) ? 0.5 : 1 }}>
            {msgType === "action" && myRole === "broker" ? "Assign" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}
